import { KeyboardEvent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout, LayoutStorage, PanelImperativeHandle, useDefaultLayout, usePanelRef } from 'react-resizable-panels';
import { ArrowLeft, ExternalLink, Eye, Files, History, Plus, Save, Send, Settings, Sparkles } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { relativeTime, shortName } from '@/frontend/lib/people';
import { cn } from '@/frontend/lib/utils';
import { ReportAssetsProvider } from '@/frontend/reports/primitives';
import { useReportBundleStore } from '@/frontend/store/ReportBundleStore';
import { useSchemaStore } from '@/frontend/store/SchemaStore';
import { INPUT_SHEET_FILE, sameSheet, sheetIdChanges } from '@/reports/bundles/inputSheet';
import { ReportBundleDiagnostic } from '@/reports/bundles/types';
import { sampleRunReportContext } from '@/reports/bundles/fixture';
import { InputSheet, InputValues, emptyInputs, inputsContext, sheetInputs, sheetProgress } from '@/reports/inputs';
import { LoadingSpinner } from '../../LoadingSpinner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Button, buttonVariants } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../../ui/resizable';
import { FillContextProvider } from '../../reportsV2/fillContext';
import { SheetForm } from '../../reportsV2/SheetForm';
import { BundleCodeEditor } from '../BundleCodeEditor';
import { BundlePreview } from '../BundlePreview';
import { SheetChanges } from '../BundleVersionsPanel';
import { CreateReportBundleModal } from '../CreateReportBundleModal';
import { CompareSheetsDialog } from '../designer/CompareSheets';
import { Explorer } from '../designer/Explorer';
import { parseFixture } from '../designer/FixtureView';
import { PreviewChoice, PreviewSidebar } from '../designer/PreviewSidebar';
import { SettingsDialog } from '../designer/SettingsDialog';
import { Activity, ActivityBar, FileHeader, FileTabs, FileView, KindChip, SidebarHeader, StatusBar, StatusItem, baseName, fileKind } from '../designer/designerChrome';
import { IdChangeList, idChangeLine } from '../inputs/IdChanges';
import { BundleAssistantPanel } from '../assistant/BundleAssistantPanel';
import { AssistantWorkspace } from '../assistant/assistantTools';
import { AssistantTransport } from '../assistant/transport';
import { useBundleAssistant } from '../assistant/useBundleAssistant';
import { InputsTab, readSheetFile } from '../inputs/InputsTab';
import { IconButton, IconLink, UnpublishedChip } from '../studioChrome';

/** What this browser remembers of the schema designer, read without trusting storage to be there. */
function remembered(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // only the convenience is lost
  }
}

const layoutStorage: LayoutStorage = { getItem: remembered, setItem: remember };

/** A schema on its own has its file and its history: fixtures, tests and the assistant belong to each report that reads it. */
type SideView = 'files' | 'assistant' | 'history';
const SIDE_VIEW_KEY = 'veriform.schemaStudio.sideView';
const DESIGNER_PANELS = ['side', 'main', 'preview'];
const DESIGNER_LAYOUT: Layout = { side: 17, main: 53, preview: 30 };
const VIEWS: FileView[] = ['design', 'try', 'code'];

/** What the preview shows: the form as an operator fills it, or the draft printed through a report that reads it, over one of its fixtures or what is typed in Try it. */
type PreviewSource = { kind: 'form' } | { kind: 'report'; bundleId: string; fixture: string | null };

const NO_FILES = { editable: false, upload: async () => null, traces: {} };

/** The schema assistant's replies, through the schema's own endpoint; its conversations are kept apart from the bundles' by their `schema:` key. */
const schemaAssistantTransport: AssistantTransport = (key, messages, onEvent, signal) => useSchemaStore.getState().streamAssistantReply(key.replace(/^schema:/, ''), messages, onEvent, signal);
const noUrls = async () => ({});

function fold(panel: { current: PanelImperativeHandle | null }, open: boolean) {
  try {
    if (open) panel.current?.expand();
    else panel.current?.collapse();
  } catch {
    // nothing to resize until the group has a size
  }
}

/**
 * One schema on its own, in the same designer as a report: its one file,
 * inputs.json, designed, tried or as its code; its published versions; the
 * reports that read it; and the preview, showing the form as an operator
 * fills it or the draft printed through a report that reads it. Saving stores
 * the draft — reports that follow it compile against it on their next save;
 * publishing freezes it as the next version.
 */
export function SchemaEditorPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const entry = useSchemaStore((s) => s.schemasById[id]);
  const fetchSchema = useSchemaStore((s) => s.fetchSchema);
  const saveDraft = useSchemaStore((s) => s.saveDraft);
  const publish = useSchemaStore((s) => s.publish);
  const updateMeta = useSchemaStore((s) => s.updateMeta);
  const deleteSchema = useSchemaStore((s) => s.deleteSchema);
  const bundlesById = useReportBundleStore((s) => s.bundlesById);
  const fetchBundle = useReportBundleStore((s) => s.fetchBundle);
  const [notFound, setNotFound] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<FileView>('design');
  /** what is typed in Try it, kept here so the preview reads it */
  const [tried, setTried] = useState<InputValues>({});
  const [previewWith, setPreviewWith] = useState<PreviewSource>({ kind: 'form' });
  /** a save refused for being stale: the stored draft changed since the page opened it, and the author picks which to keep */
  const [conflict, setConflict] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [creating, setCreating] = useState(false);

  // what a save sends and finds afterwards: the author may type while it saves
  const sourceRef = useRef(source);
  sourceRef.current = source;
  /** the stored draft the source was opened from or last saved as, sent with a save so it never overwrites one saved since */
  const base = useRef<string | undefined>(undefined);

  const [sideView, setSideView] = useState<SideView>(() => {
    const view = remembered(SIDE_VIEW_KEY);
    return view === 'history' || view === 'assistant' ? view : 'files';
  });
  const designerLayout = useDefaultLayout({ id: 'veriform.schemaStudio.designerLayout', panelIds: DESIGNER_PANELS, storage: layoutStorage });
  const [startLayout] = useState(() => designerLayout.defaultLayout ?? DESIGNER_LAYOUT);
  const [sideOpen, setSideOpen] = useState(() => (startLayout.side ?? 0) > 0);
  const [previewOpen, setPreviewOpen] = useState(() => (startLayout.preview ?? 0) > 0);
  const sidePanel = usePanelRef();
  const previewPanel = usePanelRef();

  const pickSide = (next: SideView) => {
    if (sideOpen && next === sideView) {
      setSideOpen(false);
      fold(sidePanel, false);
      return;
    }
    remember(SIDE_VIEW_KEY, next);
    setSideView(next);
    if (!sideOpen) {
      setSideOpen(true);
      fold(sidePanel, true);
    }
  };
  const togglePreview = () => {
    setPreviewOpen(!previewOpen);
    fold(previewPanel, !previewOpen);
  };

  useEffect(() => {
    let live = true;
    setNotFound(false);
    void fetchSchema(id).then((loaded) => { if (live) setNotFound(loaded === null); });
    return () => { live = false; };
  }, [id, fetchSchema]);

  // the stored draft is the baseline until the author changes it; a save re-baselines
  useEffect(() => {
    if (!entry || dirty) return;
    setSource(entry.schema.draftSource);
    base.current = entry.draftHash;
  }, [entry?.schema.draftUpdatedAt, entry?.schema.id, entry?.draftHash]);

  const edit = (text: string) => {
    sourceRef.current = text;
    setSource(text);
    setDirty(true);
  };

  const conflictRef = useRef(conflict);
  conflictRef.current = conflict;

  /** Save the draft as typed; the schema as saved, or null when what is on the page is not all saved. */
  const save = async () => {
    const sent = sourceRef.current;
    if (sent === null) return null;
    setSaving(true);
    try {
      const saved = await saveDraft(id, sent, base.current);
      if (!saved) return null;
      if ('conflict' in saved) {
        // read before the page renders again: the assistant's save_schema asks straight away
        conflictRef.current = true;
        setConflict(true);
        return null;
      }
      base.current = saved.entry.draftHash;
      conflictRef.current = false;
      setConflict(false);
      // only what was sent is saved: what was typed meanwhile stays unsaved
      if (sourceRef.current !== sent) return null;
      setDirty(false);
      return saved.entry.schema;
    } finally {
      setSaving(false);
    }
  };
  const handleSave = async (): Promise<boolean> => {
    const saved = await save();
    return !!saved && saved.draftProblems.length === 0;
  };

  /** A link followed in this tab with unsaved changes: saved first, and followed only once they are. */
  const leaveTo = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!dirty || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    void save().then((saved) => { if (saved) void navigate(to); });
  };

  /** A new report from this schema leaves the page once created: what is typed here is saved first, so it is neither lost nor missing from the draft the report follows. */
  const startReport = () => {
    if (!dirty) {
      setCreating(true);
      return;
    }
    void save().then((saved) => { if (saved) setCreating(true); });
  };

  /** The stored draft changed under the page: take it as it is now, or save what is here over it. */
  const resolveConflict = (keep: 'theirs' | 'mine') => {
    const current = useSchemaStore.getState().schemasById[id];
    if (!current) return;
    setConflict(false);
    base.current = current.draftHash;
    if (keep === 'mine') {
      void handleSave();
      return;
    }
    sourceRef.current = current.schema.draftSource;
    setSource(current.schema.draftSource);
    setDirty(false);
  };

  const codeFiles = useMemo(() => ({ [INPUT_SHEET_FILE]: source ?? '' }), [source]);

  // the assistant: the same loop as a report's, over this page's one file, saved as the schema's draft
  const assistantStatus = useReportBundleStore((s) => s.assistantStatus);
  const fetchAssistantStatus = useReportBundleStore((s) => s.fetchAssistantStatus);
  const fetchSdkTypes = useReportBundleStore((s) => s.fetchSdkTypes);
  const entryRef = useRef(entry);
  entryRef.current = entry;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const saveRef = useRef(save);
  saveRef.current = save;
  const workspace = useMemo<AssistantWorkspace>(() => ({
    getFiles: () => ({ [INPUT_SHEET_FILE]: sourceRef.current ?? '' }),
    setFiles: (next) => {
      if (!(INPUT_SHEET_FILE in next)) return;
      sourceRef.current = next[INPUT_SHEET_FILE];
      setSource(next[INPUT_SHEET_FILE]);
      setDirty(true);
    },
    onlyFiles: [INPUT_SHEET_FILE],
    saveSchema: async () => {
      const saved = await saveRef.current();
      if (!saved) return conflictRef.current ? { conflict: true, problems: [], inputs: null, breaks: [], latestVersion: null, readers: [] } : null;
      const current = useSchemaStore.getState().schemasById[id];
      const now = readSheetFile(saved.draftSource).sheet;
      const latest = current?.versions[0] ?? null;
      return {
        problems: saved.draftProblems,
        inputs: now ? sheetInputs(now).length : null,
        breaks: latest && now ? sheetIdChanges(latest.sheet, now).map(idChangeLine) : [],
        latestVersion: latest?.number ?? null,
        readers: (current?.usedBy ?? []).map((u) => u.title),
      };
    },
    // a schema has no draft to compile and no run to preview: its tools are the editor, save_schema and the SDK's source
    compile: async () => null,
    getCompiled: () => ({ compiled: null, files: {} }),
    getContext: () => sampleRunReportContext(),
    isSampleContext: () => true,
    loadRun: async () => 'A schema reads no run.',
    getSdkFiles: () => useReportBundleStore.getState().sdkTypes?.files ?? {},
  }), [id]);
  const assistant = useBundleAssistant({
    bundleId: `schema:${id}`,
    workspace,
    transport: schemaAssistantTransport,
    studioState: () => {
      const current = entryRef.current;
      return {
        subject: 'schema',
        bundleKey: current?.schema.key ?? '',
        bundleTitle: current?.schema.title ?? '',
        activeFile: INPUT_SHEET_FILE,
        previewRun: '',
        compiles: (current?.schema.draftProblems.length ?? 0) === 0,
        problems: current?.schema.draftProblems.length ?? 0,
        dirtyFiles: dirtyRef.current ? [INPUT_SHEET_FILE] : [],
        readers: (current?.usedBy ?? []).map((u) => u.title),
      };
    },
  });
  // asked for only once the assistant is shown: most visits to a schema never open it
  const assistantShown = sideOpen && sideView === 'assistant';
  useEffect(() => {
    if (!assistantShown) return;
    if (assistantStatus === null) void fetchAssistantStatus();
    if (useReportBundleStore.getState().sdkTypes === null) void fetchSdkTypes();
  }, [assistantShown]);
  const sheet = useMemo(() => readSheetFile(source ?? undefined).sheet, [source]);
  // the report the preview prints through, read again when a save or publish of this schema let go of it (its inputs.json is this draft)
  const previewBundleId = previewWith.kind === 'report' ? previewWith.bundleId : null;
  const previewBundleGone = !!previewBundleId && !bundlesById[previewBundleId];
  useEffect(() => {
    if (previewBundleId && previewBundleGone) void fetchBundle(previewBundleId);
  }, [previewBundleId, previewBundleGone]);

  /** The reports that read this schema, read when the preview's menu is opened, to print the draft through. */
  const readers = entry?.usedBy ?? [];
  const loadReaders = () => {
    for (const reader of readers) {
      if (!useReportBundleStore.getState().bundlesById[reader.reportBundleId]) void fetchBundle(reader.reportBundleId);
    }
  };

  if (notFound) {
    return (
      <div data-slot="report-studio" className="flex flex-col items-start gap-2 p-[1.5rem] text-sm">
        <p className="text-muted-foreground">{I18n.get('modelNotFound', { model: I18n.get('schema') })}</p>
        <Link to="/report-studio" className={buttonVariants({ variant: 'outline', size: 'sm' })}><ArrowLeft /> {I18n.get('reportBundleBackToList')}</Link>
      </div>
    );
  }
  if (!entry || source === null) {
    return <div className="flex justify-center py-10" aria-label={I18n.get('loading')}><LoadingSpinner /></div>;
  }

  const { schema, versions, usedBy } = entry;
  const latest = versions[0] ?? null;
  // the problems as last saved, where the designer looks for them
  const diagnostics: ReportBundleDiagnostic[] = schema.draftProblems.map((message) => ({ file: INPUT_SHEET_FILE, line: null, column: null, message }));
  const ok = schema.draftProblems.length === 0;
  const unpublished = !!latest && !(sheet && sameSheet(latest.sheet, sheet));
  const breaks = latest && sheet ? sheetIdChanges(latest.sheet, sheet).length : 0;
  const inputCount = sheet ? sheetInputs(sheet).length : 0;
  const savedState = saving ? I18n.get('saving') : dirty ? I18n.get('reportBundleUnsaved') : I18n.get('designerSavedAgo', { ago: relativeTime(schema.draftUpdatedAt ?? new Date()) });
  const draftLine = I18n.get('designerSchemaLine', { title: schema.title, which: I18n.get('designerDraft') });

  const activities: Activity<SideView>[] = [
    { id: 'files', label: I18n.get('reportBundleFiles'), icon: Files, mark: ok ? null : 'bad' },
    { id: 'assistant', label: I18n.get('reportAssistant'), icon: Sparkles, mark: assistant.running ? 'busy' : null },
    { id: 'history', label: I18n.get('designerHistory'), icon: History },
  ];

  // the preview: the form, or the draft through a report that follows it (one fixed at a version would not print this draft)
  const following = usedBy.filter((u) => {
    const loaded = bundlesById[u.reportBundleId];
    return !loaded || !loaded.schema || loaded.schema.pinnedVersion === null;
  });
  const reader = previewWith.kind === 'report' ? bundlesById[previewWith.bundleId] ?? null : null;
  const readerTitle = previewWith.kind === 'report' ? usedBy.find((u) => u.reportBundleId === previewWith.bundleId)?.title ?? '' : '';
  const previewLabel = previewWith.kind === 'form'
    ? I18n.get('designerPreviewForm')
    : I18n.get('designerReportWith', { report: readerTitle, with: previewWith.fixture ? baseName(previewWith.fixture) : I18n.get('designerPreviewTry') });
  const readerChoices: PreviewChoice[] = following.flatMap((u) => {
    const loaded = bundlesById[u.reportBundleId];
    const fixtures = Object.keys(loaded?.bundle.draftFiles ?? {}).filter((p) => fileKind(p) === 'fixture').sort();
    const pick = (fixture: string | null): PreviewChoice => ({
      key: `${u.reportBundleId}:${fixture ?? 'try'}`,
      label: I18n.get('designerReportWith', { report: u.title, with: fixture ? baseName(fixture) : I18n.get('designerPreviewTry') }),
      checked: previewWith.kind === 'report' && previewWith.bundleId === u.reportBundleId && previewWith.fixture === fixture,
      onSelect: () => setPreviewWith({ kind: 'report', bundleId: u.reportBundleId, fixture }),
    });
    return [...fixtures.map(pick), pick(null)];
  });

  const preview = () => {
    if (!sheet) return <p className="p-3 text-xs text-muted-foreground">{I18n.get('designerPreviewNoSheet')}</p>;
    if (previewWith.kind === 'form') return <FormPreview title={schema.title} sheet={sheet} inputs={tried} />;
    if (!reader) return <div className="flex justify-center py-10" aria-label={I18n.get('loading')}><LoadingSpinner /></div>;
    const fixture = previewWith.fixture ? parseFixture(reader.bundle.draftFiles[previewWith.fixture]) : null;
    return <BundlePreview compiled={reader.bundle.draftCompiled} context={inputsContext(sheet, previewWith.fixture ? fixture?.inputs ?? {} : tried, fixture?.traces)} values={fixture?.values} stale={dirty} />;
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's' && !e.defaultPrevented) {
      e.preventDefault();
      if (dirty && !saving) void handleSave();
    }
  };

  return (
    <div data-slot="report-studio" className="flex h-svh min-w-0 flex-col bg-background text-sm text-foreground" style={{ contain: 'inline-size' }} onKeyDown={onKeyDown}>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b px-2">
        <IconLink label={I18n.get('reportBundleBackToList')} to="/report-studio" onClick={leaveTo('/report-studio')}><ArrowLeft /></IconLink>
        <KindChip kind="schema" />
        <h1 className="min-w-16 truncate text-sm font-semibold" title={schema.description || schema.title}>{schema.title}</h1>
        <span className="hidden shrink-0 text-xs whitespace-nowrap text-muted-foreground sm:inline">
          {latest ? `v${latest.number}${latest.createdAt ? ` · ${new Date(latest.createdAt).toLocaleDateString()}` : ''}` : I18n.get('reportBundleNeverPublished')}
        </span>
        {unpublished && <UnpublishedChip />}
        <span className="ml-auto hidden text-xs whitespace-nowrap text-muted-foreground md:inline">{savedState}</span>
        <Button variant="outline" size="sm" onClick={() => void handleSave()} disabled={!dirty || saving} aria-label={I18n.get('reportBundleSaveDraft')}>
          <Save /> <span className="hidden lg:inline">{I18n.get('reportBundleSaveDraft')}</span>
        </Button>
        <Button variant="outline" size="sm" aria-pressed={previewOpen} onClick={togglePreview} className={cn(previewOpen && 'bg-muted')}>
          <Eye /> {I18n.get('reportBundlePreview')}
        </Button>
        <IconButton label={I18n.get('designerSchemaSettings')} onClick={() => setShowSettings(true)}><Settings /></IconButton>
        <Button size="sm" onClick={() => setShowPublish(true)} disabled={saving} aria-label={I18n.get('reportBundlePublishVersion')}>
          <Send /> {I18n.get('designerPublishSchemaNext', { number: String((latest?.number ?? 0) + 1) })}
        </Button>
      </header>
      {conflict && (
        <div role="alert" className="flex shrink-0 flex-wrap items-center gap-2 border-b border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-950">
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            {schema.draftUpdatedBy && schema.draftUpdatedAt
              ? I18n.get('designerConflictBy', { who: shortName(schema.draftUpdatedBy), title: schema.title, ago: relativeTime(schema.draftUpdatedAt) })
              : I18n.get('schemaDraftConflict')}
          </span>
          <Button variant="outline" size="xs" onClick={() => setComparing(true)} disabled={saving}>{I18n.get('designerCompare')}</Button>
          <Button variant="outline" size="xs" onClick={() => resolveConflict('theirs')} disabled={saving}>{I18n.get('schemaDraftConflictTheirs')}</Button>
          <Button variant="outline" size="xs" onClick={() => resolveConflict('mine')} disabled={saving}>{I18n.get('rv2SheetConflictMine')}</Button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <ActivityBar items={activities} active={sideOpen ? sideView : null} onPick={pickSide} />
        <ResizablePanelGroup id="schema-designer" className="min-h-0 flex-1" defaultLayout={startLayout} onLayoutChanged={designerLayout.onLayoutChanged}>
          <ResizablePanel id="side" panelRef={sidePanel} groupResizeBehavior="preserve-pixel-size" collapsible collapsedSize={0} minSize={220} onResize={(size) => setSideOpen(size.asPercentage > 0)}>
            <div className="h-full" inert={!sideOpen}>
              {/* the assistant stays mounted under the other views: it keeps working while the author looks elsewhere */}
              <div className="h-full" hidden={sideView !== 'assistant'} inert={sideView !== 'assistant'}>
                <BundleAssistantPanel assistant={assistant} status={assistantStatus} files={codeFiles} previewLabel="" subject="schema" onClose={() => pickSide('assistant')} />
              </div>
              {sideView === 'assistant' ? null : sideView === 'files' ? (
                <Explorer
                  paths={[INPUT_SHEET_FILE]}
                  active={INPUT_SHEET_FILE}
                  working={codeFiles}
                  saved={{ [INPUT_SHEET_FILE]: entry.schema.draftSource }}
                  published={null}
                  changedSincePublished={() => unpublished}
                  results={[]}
                  schemaMeta={`${schema.title} · ${I18n.get('designerDraft')}`}
                  undeletable={[INPUT_SHEET_FILE]}
                  onOpen={() => undefined}
                  extra={<ReadBy usedBy={usedBy} leaveTo={leaveTo} onNew={startReport} />}
                />
              ) : (
                <div className="flex h-full flex-col bg-muted/30">
                  <SidebarHeader title={I18n.get('designerHistory')} />
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {versions.length === 0 ? (
                      <p className="p-[1rem] text-xs text-muted-foreground">{I18n.get('reportBundleNoVersions')}</p>
                    ) : (
                      <ol aria-label={I18n.get('designerVersions')} className="divide-y">
                        {versions.map((v, i) => (
                          <li key={v.id} className="flex flex-col gap-1 px-3 py-2.5 text-xs">
                            <span className="text-sm font-medium">v{v.number}</span>
                            <span className="text-muted-foreground" title={v.publishedBy}>{new Date(v.createdAt ?? '').toLocaleString()} · {shortName(v.publishedBy)}</span>
                            {v.note && <span className="[overflow-wrap:anywhere]">{v.note}</span>}
                            <SheetChanges sheet={v.sheet} previous={versions[i + 1]?.sheet} />
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
              )}
            </div>
          </ResizablePanel>
          <ResizableHandle />

          <ResizablePanel id="main" minSize={360}>
            <div className="flex h-full min-w-0 flex-col" style={{ contain: 'inline-size' }}>
              <FileTabs paths={[INPUT_SHEET_FILE]} active={INPUT_SHEET_FILE} unsaved={() => dirty} onPick={() => undefined} />
              <FileHeader path={INPUT_SHEET_FILE} note={draftLine} views={VIEWS} view={view} onView={setView}>
                <span className="hidden text-xs text-muted-foreground lg:inline">
                  {usedBy.length ? I18n.get('designerSchemaReachesReports', { reports: usedBy.map((u) => u.title).join(', ') }) : I18n.get('designerSchemaNoReaders')}
                </span>
              </FileHeader>
              <div className="relative min-h-0 flex-1">
                {view !== 'code' && (
                  <div className="h-full">
                    <InputsTab text={source} diagnostics={diagnostics} mode={view === 'try' ? 'try' : 'design'} onChange={edit} onOpenCode={() => setView('code')} tried={tried} onTry={setTried} />
                  </div>
                )}
                <div className="h-full" hidden={view !== 'code'} aria-label={I18n.get('reportBundleEditor')}>
                  <BundleCodeEditor files={codeFiles} active={INPUT_SHEET_FILE} onChange={(_path, text) => edit(text)} diagnostics={diagnostics} sdkTypes={null} onSave={() => void handleSave()} />
                </div>
              </div>
            </div>
          </ResizablePanel>
          <ResizableHandle />

          <ResizablePanel id="preview" panelRef={previewPanel} groupResizeBehavior="preserve-pixel-size" collapsible collapsedSize={0} minSize={300} onResize={(size) => setPreviewOpen(size.asPercentage > 0)}>
            <div className="h-full" inert={!previewOpen} style={{ contain: 'inline-size' }}>
              <PreviewSidebar
                label={previewLabel}
                onOpenMenu={loadReaders}
                onHide={togglePreview}
                groups={[
                  { key: 'form', choices: [{ key: 'form', label: I18n.get('designerPreviewFormLong'), checked: previewWith.kind === 'form', onSelect: () => setPreviewWith({ kind: 'form' }) }] },
                  { key: 'readers', label: I18n.get('designerPreviewThroughReport'), choices: readerChoices, note: I18n.get('designerPreviewThroughReportNote') },
                ]}
              >
                {preview()}
              </PreviewSidebar>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      <StatusBar>
        <StatusItem tone={ok ? 'ok' : 'bad'} onClick={ok ? undefined : () => setView('code')}>{I18n.get(ok ? 'schemaDraftOk' : 'schemaDraftProblems')}</StatusItem>
        {sheet && <StatusItem>{I18n.get(inputCount === 1 ? 'designerSchemaInputsOne' : 'designerSchemaInputs', { count: String(inputCount) })}</StatusItem>}
        {latest && breaks > 0 && <StatusItem tone="warn">{I18n.get(breaks === 1 ? 'designerBreaksRecordsOne' : 'designerBreaksRecords', { count: String(breaks), number: String(latest.number) })}</StatusItem>}
        <StatusItem className="hidden md:flex">{I18n.get('schemaReadBy')}: {usedBy.length ? usedBy.map((u) => u.title).join(', ') : I18n.get('schemaReadByNone')}</StatusItem>
        {previewOpen && <StatusItem className="hidden lg:flex">{I18n.get('designerPreviewing', { source: previewLabel })}</StatusItem>}
        <StatusItem className="ml-auto">{savedState}</StatusItem>
      </StatusBar>

      {comparing && conflict && (
        <CompareSheetsDialog
          title={schema.title}
          theirs={schema.draftSource}
          mine={source}
          by={schema.draftUpdatedBy}
          at={schema.draftUpdatedAt}
          busy={saving}
          onTheirs={() => { setComparing(false); resolveConflict('theirs'); }}
          onMine={() => { setComparing(false); resolveConflict('mine'); }}
          onHide={() => setComparing(false)}
        />
      )}
      {showPublish && (
        <PublishSchemaDialog
          title={schema.title}
          source={source}
          latest={latest}
          readers={usedBy.length}
          problems={dirty ? [] : schema.draftProblems}
          onHide={() => setShowPublish(false)}
          onPublish={async (note) => {
            if (dirty && !(await handleSave())) return false;
            // the draft the dialog compared: one saved elsewhere since is refused and shown here instead
            const version = await publish(id, note, base.current);
            if (version && (!sideOpen || sideView !== 'history')) pickSide('history');
            return !!version;
          }}
        />
      )}
      {showSettings && (
        <SettingsDialog
          kind="schema"
          title={schema.title}
          description={schema.description}
          onHide={() => setShowSettings(false)}
          onSave={(data) => updateMeta(id, data)}
          onDelete={() => { setShowSettings(false); setShowDelete(true); }}
        />
      )}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{I18n.get('schemaDelete')}</AlertDialogTitle>
            <AlertDialogDescription>{I18n.get('schemaDeleteConfirm', { title: schema.title })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{I18n.get('cancel')}</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => { setShowDelete(false); void deleteSchema(id).then((deleted) => { if (deleted) void navigate('/report-studio'); }); }}>{I18n.get('delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {creating && <CreateReportBundleModal schemaId={id} onHide={() => setCreating(false)} />}
    </div>
  );
}

/** Under the schema's file: the reports that read it, each a way into its own designer, and a new one started from it. */
function ReadBy({ usedBy, leaveTo, onNew }: { usedBy: { reportBundleId: string; title: string }[]; leaveTo: (to: string) => (e: MouseEvent<HTMLAnchorElement>) => void; onNew: () => void }) {
  return (
    <section aria-label={I18n.get('schemaReadBy')} className="mt-1">
      <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-1 text-[0.65rem] font-semibold tracking-wide text-muted-foreground uppercase">
        <span>{I18n.get('schemaReadBy')}</span>
        <span className="font-normal tracking-normal normal-case">{I18n.get(usedBy.length === 1 ? 'designerReportCountOne' : 'designerReportCount', { count: String(usedBy.length) })}</span>
      </div>
      <ul>
        {usedBy.map((u) => (
          <li key={u.reportBundleId}>
            <Link to={`/report-studio/${u.reportBundleId}`} onClick={leaveTo(`/report-studio/${u.reportBundleId}`)} className="flex items-center gap-2 py-1 pr-2 pl-4 text-[0.8rem] hover:bg-muted">
              <span aria-hidden="true" className="inline-flex h-4 w-7 shrink-0 items-center justify-center rounded-sm bg-amber-100 text-[0.55rem] font-bold tracking-wide text-amber-900">RPT</span>
              <span className="min-w-0 flex-1 truncate">{u.title}</span>
              <ExternalLink aria-hidden="true" className="size-3 shrink-0 text-muted-foreground" />
            </Link>
          </li>
        ))}
      </ul>
      {usedBy.length === 0 && <p className="px-5 py-1 text-xs text-muted-foreground">{I18n.get('designerSchemaNoReaders')}</p>}
      <Button variant="ghost" size="xs" className="mt-1 ml-3 text-primary" onClick={onNew}><Plus /> {I18n.get('designerNewReportFromSchema')}</Button>
      <p className="px-4 pt-2 text-xs text-muted-foreground">{I18n.get('designerSchemaNoFixtures')}</p>
    </section>
  );
}

/** The form as an operator fills it in Reports v2, with what is typed in Try it: shown, not filled, here. */
function FormPreview({ title, sheet, inputs }: { title: string; sheet: InputSheet; inputs: InputValues }) {
  const values = useMemo(() => ({ ...emptyInputs(sheet), ...inputs }), [sheet, inputs]);
  const progress = useMemo(() => sheetProgress(sheet, values), [sheet, values]);
  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="text-xs text-muted-foreground">{I18n.get('designerFormPreviewHeading', { title })}</div>
      <FillContextProvider value={NO_FILES}>
        <ReportAssetsProvider resolve={noUrls}>
          <SheetForm sheet={sheet} inputs={values} progress={progress.sections} onChange={() => undefined} />
        </ReportAssetsProvider>
      </FillContextProvider>
    </div>
  );
}

/** Publishing the draft: what it breaks since the latest version, how many reports read the schema, and what keeps it from being published. */
function PublishSchemaDialog({ title, source, latest, readers, problems, onHide, onPublish }: {
  title: string;
  source: string;
  latest: { number: number; sheet: Parameters<typeof sheetIdChanges>[0] } | null;
  readers: number;
  /** what is wrong with the draft as saved; empty while there are unsaved changes, which publishing saves first */
  problems: string[];
  onHide: () => void;
  /** whether it was published; the dialog stays open when not */
  onPublish: (note: string) => Promise<boolean>;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { unchanged, changes } = useMemo(() => {
    const now = readSheetFile(source).sheet;
    if (!now || !latest) return { unchanged: false, changes: [] };
    return { unchanged: sameSheet(latest.sheet, now), changes: sheetIdChanges(latest.sheet, now) };
  }, [source, latest]);
  const blocked = unchanged || problems.length > 0;
  const submit = () => {
    if (busy || blocked) return;
    setBusy(true);
    void onPublish(note).then((published) => { setBusy(false); if (published) onHide(); }, () => setBusy(false));
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onHide(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{I18n.get('reportBundlePublishVersion')}</DialogTitle>
          <DialogDescription>{I18n.get('schemaPublishConfirm', { title, number: String((latest?.number ?? 0) + 1), count: String(readers) })}</DialogDescription>
        </DialogHeader>
        {unchanged ? (
          <p className="text-sm text-muted-foreground">{I18n.get('schemaUnchanged', { number: String(latest?.number ?? '') })}</p>
        ) : (
          <div className="grid gap-2">
            <Label htmlFor="publish-schema-note">{I18n.get('reportBundlePublishNote')}</Label>
            <Input id="publish-schema-note" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
          </div>
        )}
        {latest && changes.length > 0 && (
          <div role="alert" className="flex flex-col gap-1.5 rounded-lg border-[1px] border-amber-300 bg-amber-50 p-[0.75rem] text-sm text-amber-950">
            <p className="font-medium">{I18n.get('rv2IdChangesTitle', { title, number: String(latest.number) })}</p>
            <IdChangeList changes={changes} shown={8} />
            <p className="text-xs">{I18n.get('rv2IdChangesHelp')}</p>
          </div>
        )}
        {problems.length > 0 && (
          <div role="alert" className="flex flex-col gap-1.5 rounded-lg border-[1px] border-destructive/40 bg-destructive/5 p-[0.75rem] text-sm text-destructive">
            <p className="font-medium">{I18n.get('schemaDraftBroken', { title })}</p>
            <ul className="list-disc pl-[1.25rem] text-xs [overflow-wrap:anywhere]">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onHide} disabled={busy}>{I18n.get('cancel')}</Button>
          <Button disabled={busy || blocked} onClick={submit}>{I18n.get('publish')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
